import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import SvgFormat from 'common/helpers/svgFormat';
import hexToRGBA from '~helpers/hex-to-rgba';
import event from '~helpers/mixpanel';
import OnboardingContext from '../context';
import { pageType } from '~constants/pages';

const useStyles = makeStyles(theme => ({
  wrap: {
    position: 'relative',
    width: '360px',
    textAlign: 'center',
    margin: '0 22px',
    padding: '76px 48px 84px',
    backgroundColor: theme.colors.gray1,
    border: `2px solid ${theme.colors.gray1}`,
    borderRadius: theme.borderRadius.min,
    cursor: 'pointer',
  },
  selectedWrap: {
    border: `2px solid ${theme.colors.mainGreen6}`,
    boxShadow: `0 12px 32px 2px ${hexToRGBA(theme.colors.blue6, 0.19)}`,
  },
  name: {
    ...theme.typography.h3,
    color: hexToRGBA(theme.colors.gray10, 0.85),
    marginTop: '14px',
    marginBottom: '16px',
  },
  description: {
    fontSize: '12px',
    lineHeight: '20px',
    color: hexToRGBA(theme.colors.gray10, 0.65),
  },
  icon: {
    width: '102px',
    height: '96px',
  },
  tickWrap: {
    position: 'absolute',
    top: '-16px',
    right: '-16px',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '32px',
    height: '32px',
    border: `1px solid ${theme.colors.gray1}`,
    borderRadius: '50%',
    backgroundColor: theme.colors.mainGreen6,
  },
  tick: {
    width: '16px',
    height: '12px',
    fill: theme.colors.gray1,
  },
}));

const ResidentTypeItem = ({ residentType = '' }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const onboardingData = useContext(OnboardingContext);

  const [hovered, setHovered] = useState(false);

  const isSelected = useMemo(() => {
    return residentType === onboardingData?.residentTypeSlug;
  }, [onboardingData, residentType]);

  const handleClickResidentType = useCallback(() => {
    onboardingData?.setResidentTypeSlug(residentType);

    event('Clicked OP Resident Type Page', {
      'Clicked Position': 'Resident Type',
      'Resident Type': t(`landlord.onboarding.resident_type.${residentType.toLowerCase()}`),
      'Property Type': onboardingData.propertyTypeSlug,
      'Current Page Type': pageType.onBoardingPage,
    });
  }, [onboardingData, residentType, t]);

  return (
    <div
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      className={classNames(classes.wrap, { [classes.selectedWrap]: isSelected || hovered })}
      onClick={handleClickResidentType}
    >
      <If condition={isSelected}>
        <span className={classes.tickWrap}>
          <SvgFormat className={classes.tick} type="tick" />
        </span>
      </If>
      <SvgFormat
        type={`resident-type-${residentType.toLowerCase()}${isSelected ? '-selected' : ''}`}
        className={classes.icon}
      />
      <div className={classes.name}>
        {t(`landlord.onboarding.resident_type.${residentType.toLowerCase()}`)}
      </div>
      <div className={classes.description}>
        {t(`landlord.onboarding.resident_type.description.${residentType.toLowerCase()}`)}
      </div>
    </div>
  );
};

ResidentTypeItem.propTypes = {
  residentType: PropTypes.string.isRequired,
};

export default React.memo(ResidentTypeItem);
